// Balance and timing constants for Phase 0 / Milestone 1 ("REACTOR RESERVE EVENT 0x01").
// Every number that shapes the pressure curve lives here and is restated as a table in
// GAME_DESIGN.md Appendix A. Change both in the same commit.

export const TICK_HZ = 10;
export const TICK_MS = 1000 / TICK_HZ;

/** Mission length: 300 s. Keep the reactor alive this long to win. */
export const MISSION_TICKS = 300 * TICK_HZ;

export const RESERVE_START = 75;
export const RESERVE_MAX = 100;
/** Base drain: 0.4 %/s → idle death from 75 % at ~T+3:07. */
export const DRAIN_PER_TICK = 0.04;
/** While a breaker is tripped the drain is multiplied (1.0 %/s). */
export const TRIP_MULTIPLIER = 2.5;

/** A freshly reset panel is safe for at least this long, even if the next trip was overdue. */
export const TRIP_MIN_GAP_TICKS = 10 * TICK_HZ;
/** Wrong switch in the reset procedure → panel lockout. */
export const TOGGLE_LOCKOUT_TICKS = 2 * TICK_HZ;

/** Two-person Hold ("surge dump"): both handles held for this long. */
export const HOLD_REQUIRED_TICKS = 5 * TICK_HZ;
export const HOLD_SUCCESS_GAIN = 25;
export const HOLD_FAIL_PENALTY = 8;
export const HOLD_COOLDOWN_SUCCESS_TICKS = 35 * TICK_HZ;
export const HOLD_COOLDOWN_FAIL_TICKS = 15 * TICK_HZ;
/** A holder whose link drops keeps the handle for this long (browser refresh must not fail the hold). */
export const HOLD_DROP_GRACE_TICKS = 3 * TICK_HZ;

export const RESERVE_LOW = 25;
export const RESERVE_CRITICAL = 10;

export const RECONNECT_WINDOW_S = 60;
export const MIN_CREW_TO_START = 2;
export const MAX_CREW = 8;
export const PASSWORD_MIN = 4;
export const NAME_MAX = 16;

/** Hull code: what crew type to board. `ABCD-1234` — four letters (no I/O), four digits, case-insensitive on input. */
export const HULL_LETTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ";
export const HULL_DIGITS = "0123456789";
export const HULL_CODE_LENGTH = 9; // including the dash
/** Development shortcut: one fixed hull everyone can board with one press. Remove before the public phase. */
export const DEV_HULL_CODE = "DEVH-0000";
export const DEV_HULL_PASSWORD = "devdev";

/** Power allocation (M3). Output shrinks with the reserve; demand is always larger than output. */
export const POWER_MAX_PER_SYSTEM = 4;
export const POWER_DEMAND = 4;                 // each system wants 4 units to be nominal
export const POWER_OUTPUT_FULL = 9;            // units available at 100 % reserve (demand is 12)
export const POWER_OUTPUT_MIN = 5;             // units available at 0 % reserve
/** Cooling: each missing unit adds to the drain multiplier. */
export const COOLING_DRAIN_PER_MISSING = 0.15;
/** Life support: O2 per second lost per missing unit; regained slowly when fully powered. */
export const O2_START = 100;
export const O2_LOSS_PER_MISSING_PER_S = 0.35;
export const O2_REGEN_PER_S = 0.4;
export const O2_LOW = 40;
export const O2_CRITICAL = 15;
/** Comms: base link quality by allocation (0..4). */
export const COMMS_BY_ALLOC = [0.2, 0.45, 0.65, 0.82, 0.95] as const;

/** Orientation drill: the director's first "event" is a supervised surge dump before anything breaks. */
export const DRILL_AT_TICKS = 150;

/** Director (M3): event schedule window and cooldowns, in ticks. */
export const DIRECTOR_FIRST_EVENT_TICKS = 350;
export const DIRECTOR_MIN_GAP_TICKS = 300;
export const DIRECTOR_MAX_GAP_TICKS = 650;
/** Coolant leak: two-person hold, drain ×1.6 until resolved. */
export const COOLANT_REQUIRED_TICKS = 80;
export const COOLANT_DRAIN_MULT = 1.4;
export const COOLANT_FAIL_PENALTY = 5;
/** Relay damage: comms quality × this until a spare from storage is socketed at the bridge. */
export const RELAY_DAMAGED_FACTOR = 0.3;

/** Intercom link model (M2). commsQuality = COMMS_BASE × transit × breaker × reserve, clamped 0.05..1. */
export const COMMS_BASE = 0.92;
export const COMMS_TRANSIT_FACTOR = 0.45;     // corridor relays are bad
export const COMMS_TRIPPED_FACTOR = 0.7;      // tripped breaker browns out the comms bus
export const COMMS_LOW_RESERVE_FACTOR = 0.8;  // below RESERVE_LOW
export const COMMS_MIN = 0.05;
/** Per-hop attenuation applied on the receiving client between listener and speaker (deck distance). */
export const COMMS_HOP_FACTOR = 0.85;

/** Sentinel for "no further trips scheduled" (fits uint32). */
export const NEVER_TICK = 0xffffffff;
